<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useQuickLinksStore, quickLinkIconUrl, type QuickLink } from '@/stores/quickLinks'

// Nombre d'icônes visibles dans la topbar ; le reste passe dans le menu « plus »
const MAX_VISIBLE = 8

const store = useQuickLinksStore()
const showMore = ref(false)
const rootEl = ref<HTMLElement | null>(null)
const brokenIcons = ref<Set<number>>(new Set())

const visibleLinks = computed(() => store.links.slice(0, MAX_VISIBLE))
const overflowLinks = computed(() => store.links.slice(MAX_VISIBLE))

const iconUrl = (link: QuickLink) => quickLinkIconUrl(link)
const initial = (link: QuickLink) => link.label.trim().charAt(0).toUpperCase()

const closeOnOutsideClick = (e: MouseEvent) => {
  if (rootEl.value && !rootEl.value.contains(e.target as Node)) showMore.value = false
}

onMounted(() => {
  if (!store.loaded) store.loadActiveLinks()
  document.addEventListener('click', closeOnOutsideClick)
})
onBeforeUnmount(() => document.removeEventListener('click', closeOnOutsideClick))
</script>

<template>
  <div v-if="store.links.length" ref="rootEl" class="quick-links">
    <template v-for="link in visibleLinks" :key="link.id">
      <router-link v-if="link.is_internal" :to="link.url" class="quick-link" :title="link.label" :aria-label="link.label">
        <img v-if="iconUrl(link) && !brokenIcons.has(link.id)" :src="iconUrl(link)" alt="" class="quick-link-icon" @error="brokenIcons.add(link.id)" />
        <span v-else class="quick-link-fallback">{{ initial(link) }}</span>
      </router-link>
      <a v-else :href="link.url" target="_blank" rel="noopener noreferrer" class="quick-link" :title="link.label" :aria-label="link.label">
        <img v-if="iconUrl(link) && !brokenIcons.has(link.id)" :src="iconUrl(link)" alt="" class="quick-link-icon" @error="brokenIcons.add(link.id)" />
        <span v-else class="quick-link-fallback">{{ initial(link) }}</span>
      </a>
    </template>

    <div v-if="overflowLinks.length" class="relative">
      <button class="quick-link" title="Plus de liens" aria-label="Plus de liens" @click="showMore = !showMore">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 12h.01M12 12h.01M19 12h.01" />
        </svg>
      </button>
      <div v-if="showMore" class="quick-links-menu">
        <template v-for="link in overflowLinks" :key="link.id">
          <router-link v-if="link.is_internal" :to="link.url" class="quick-links-menu-item" @click="showMore = false">
            <img v-if="iconUrl(link) && !brokenIcons.has(link.id)" :src="iconUrl(link)" alt="" class="w-4 h-4 object-contain" @error="brokenIcons.add(link.id)" />
            <span v-else class="quick-link-fallback quick-link-fallback--sm">{{ initial(link) }}</span>
            <span class="truncate">{{ link.label }}</span>
          </router-link>
          <a v-else :href="link.url" target="_blank" rel="noopener noreferrer" class="quick-links-menu-item" @click="showMore = false">
            <img v-if="iconUrl(link) && !brokenIcons.has(link.id)" :src="iconUrl(link)" alt="" class="w-4 h-4 object-contain" @error="brokenIcons.add(link.id)" />
            <span v-else class="quick-link-fallback quick-link-fallback--sm">{{ initial(link) }}</span>
            <span class="truncate">{{ link.label }}</span>
          </a>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.quick-links {
  @apply flex items-center gap-1 pr-3 mr-1 border-r border-gray-200;
}
.quick-link {
  @apply flex items-center justify-center w-8 h-8 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors;
}
.quick-link-icon {
  @apply w-5 h-5 object-contain;
}
.quick-link-fallback {
  @apply flex items-center justify-center w-5 h-5 rounded-full bg-primary text-white text-xs font-semibold;
}
.quick-link-fallback--sm {
  @apply w-4 h-4 text-[10px];
}
.quick-links-menu {
  @apply absolute right-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-gray-200 py-1 min-w-48 max-w-72 z-50;
}
.quick-links-menu-item {
  @apply flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100;
}
</style>
